"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

// Define button option type
export interface ChatButtonOption {
  id: string;
  text: string;
  value: string;
  action?: string;
  nextFlow?: string;
}

// Update ChatMessage to include buttons and HTML support
export interface ChatMessage {
  id: string;
  text: string;
  type: "user" | "bot" | "system";
  timestamp: number;
  buttons?: ChatButtonOption[];
  html?: boolean;
  metadata?: Record<string, any>;
}

// Flow definition for structured conversations
export interface ChatFlow {
  id: string;
  messages: Omit<ChatMessage, "id" | "timestamp">[];
  options?: ChatButtonOption[];
}

export interface ChatState {
  messages: ChatMessage[];
  isTyping: boolean;
  isOpen: boolean;
  isMinimized: boolean;
  currentFlow: string | null;
  availableOptions: ChatButtonOption[];
}

export interface ChatOptions {
  initialMessages?: ChatMessage[];
  persistKey?: string;
  flows?: Record<string, ChatFlow>;
  initialFlow?: string;
  typingDelay?: number;
  initiallyOpen?: boolean;
}

export function useChat(options: ChatOptions = {}) {
  const {
    initialMessages = [],
    persistKey,
    flows = {},
    initialFlow = null,
    typingDelay = 1000,
    initiallyOpen = false,
  } = options;

  const [state, setState] = useState<ChatState>({
    messages: initialMessages,
    isTyping: false,
    isOpen: initiallyOpen,
    isMinimized: false,
    currentFlow: initialFlow,
    availableOptions: (initialFlow && flows[initialFlow]?.options) || [],
  });

  // Track if component is mounted
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Load persisted messages if available
  useEffect(() => {
    if (persistKey) {
      try {
        const savedMessages = localStorage.getItem(persistKey);
        if (savedMessages) {
          setState((prev) => ({
            ...prev,
            messages: JSON.parse(savedMessages),
          }));
        }
      } catch (error) {
        console.error("Error loading persisted chat messages:", error);
      }
    }
  }, [persistKey]);

  // Save messages to localStorage when they change
  useEffect(() => {
    if (persistKey && state.messages.length > 0) {
      try {
        localStorage.setItem(persistKey, JSON.stringify(state.messages));
      } catch (error) {
        console.error("Error persisting chat messages:", error);
      }
    }
  }, [state.messages, persistKey]);

  // Initialize first flow if provided
  useEffect(() => {
    if (initialFlow && flows[initialFlow] && state.messages.length === 0) {
      startFlow(initialFlow);
    }
  }, [initialFlow, flows]);

  const addMessage = useCallback(
    (message: Omit<ChatMessage, "id" | "timestamp">) => {
      const newMessage = {
        ...message,
        id: uuidv4(),
        timestamp: Date.now(),
      };

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
      }));

      return newMessage;
    },
    []
  );

  const updateMessage = useCallback(
    (id: string, updates: Partial<Omit<ChatMessage, "id">>) => {
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.id === id ? { ...msg, ...updates } : msg
        ),
      }));
    },
    []
  );

  const clearMessages = useCallback(() => {
    setState((prev) => ({
      ...prev,
      messages: [],
      availableOptions: prev.currentFlow
        ? flows[prev.currentFlow]?.options || []
        : [],
    }));

    if (persistKey) {
      localStorage.removeItem(persistKey);
    }
  }, [persistKey, flows]);

  // Start a specific flow
  const startFlow = useCallback(
    (flowId: string) => {
      const flow = flows[flowId];
      if (!flow) return;

      // Show typing indicator
      setState((prev) => ({ ...prev, isTyping: true, currentFlow: flowId }));

      // Add each message in the flow with a delay
      let delay = 0;
      flow.messages.forEach((message) => {
        setTimeout(() => {
          if (isMounted.current) {
            addMessage(message);

            // After the last message, set available options and turn off typing
            if (message === flow.messages[flow.messages.length - 1]) {
              setState((prev) => ({
                ...prev,
                isTyping: false,
                availableOptions: flow.options || [],
              }));
            }
          }
        }, delay);
        delay += typingDelay;
      });
    },
    [flows, typingDelay, addMessage]
  );

  // Handle button clicks
  const handleButtonClick = useCallback(
    (option: ChatButtonOption) => {
      // Add user's selection as a message
      addMessage({
        text: option.text,
        type: "user",
      });

      // If there's a next flow, start it
      if (option.nextFlow && flows[option.nextFlow]) {
        startFlow(option.nextFlow);
      }
      // Otherwise, just add the button value as a bot response
      else if (option.value) {
        // Show typing indicator
        setState((prev) => ({ ...prev, isTyping: true, availableOptions: [] }));

        setTimeout(() => {
          if (isMounted.current) {
            addMessage({
              text: option.value,
              type: "bot",
            });
            setState((prev) => ({ ...prev, isTyping: false }));
          }
        }, typingDelay);
      }
    },
    [addMessage, flows, startFlow, typingDelay]
  );

  const toggleChat = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const openChat = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: true }));
  }, []);

  const closeChat = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Add controls for minimizing the chat
  const minimizeChat = useCallback(() => {
    setState((prev) => ({ ...prev, isMinimized: true }));
  }, []);

  const maximizeChat = useCallback(() => {
    setState((prev) => ({ ...prev, isMinimized: false }));
  }, []);

  return {
    messages: state.messages,
    isTyping: state.isTyping,
    isOpen: state.isOpen,
    isMinimized: state.isMinimized,
    availableOptions: state.availableOptions,
    currentFlow: state.currentFlow,
    addMessage,
    updateMessage,
    clearMessages,
    toggleChat,
    openChat,
    closeChat,
    minimizeChat,
    maximizeChat,
    startFlow,
    handleButtonClick,
  };
}
